import { inject } from '@angular/core';
import { CanActivateFn, Params, Router, UrlTree } from '@angular/router';
import { Observable, of, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { DataStorageService } from '../services/data-storage.service';
import { ViewAsSessionService } from '../view-as/view-as-session.service';
import { CompanyCustomizationsService } from '../../services/customization/customizations.service';
import { COMMUNITY_PATH_PARAMS, resolveCommunityPath } from '../community-path.utils';

function currentQueryParams(urlParams: URLSearchParams): Params {
  const queryParams: Params = {};
  urlParams.forEach((value: string, key: string) => {
    const allValues: string[] = urlParams.getAll(key);
    queryParams[key] = allValues.length > 1 ? allValues : value;
  });
  return queryParams;
}

function decodeParamName(rawName: string): string {
  try {
    return decodeURIComponent(rawName);
  } catch {
    return rawName;
  }
}

function searchWithoutCommunityParams(): string {
  const survivors: string[] = window.location.search
    .slice(1)
    .split('&')
    .filter((pair: string) => !!pair && !COMMUNITY_PATH_PARAMS.includes(decodeParamName(pair.split('=')[0])));
  return survivors.length ? `?${survivors.join('&')}` : '';
}

const getValidReturnUrl: (tribeUrl: string | undefined) => string = (tribeUrl: string | undefined): string => {
  const currentUrl = window.location.pathname + window.location.search;
  if (window.location.pathname !== '/community' || !window.location.search) {
    return currentUrl;
  }
  if (resolveCommunityPath(currentQueryParams(new URLSearchParams(window.location.search)), tribeUrl)) {
    return currentUrl;
  }
  return `${window.location.pathname}${searchWithoutCommunityParams()}`;
};

export const AuthGuard: CanActivateFn = ():
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const dataStorage = inject(DataStorageService);
  const tribeUrl = inject(CompanyCustomizationsService).getCustomizations()?.TRIBE_URL;
  if (!authService.hasToken()) {
    void router.navigate(['/login'], {
      queryParams: { returnUrl: getValidReturnUrl(tribeUrl) }
    });
    return of(false);
  } else if (authService.checkTime(dataStorage.getExpiredTime()) <= 0) {
    // a view-as session has no refresh token, so the refresh below is doomed and its
    // failure path lands on /login — which auto-redirects into whatever real advocate
    // session localStorage holds, silently and with no banner. End the session instead.
    if (dataStorage.isViewAsSession()) {
      inject(ViewAsSessionService).handleSessionEnded();
      return of(false);
    }
    authService.refreshToken(true);
    const canActivate = authService.isTokenRefreshed.pipe(take(1));
    canActivate.subscribe({
      next: (can) => {
        if (!can) {
          void router.navigate(['/login'], {
            queryParams: { returnUrl: getValidReturnUrl(tribeUrl) }
          });
        }
      },
      error: () => {
        void router.navigate(['/login'], {
          queryParams: { returnUrl: getValidReturnUrl(tribeUrl) }
        });
      }
    });
    return canActivate;
  } else {
    return of(true);
  }
};
